import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Shuffle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { btnPrimary, btnGhost, inputCls } from "./ui-kit";

const generate = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const arr = new Uint32Array(10);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join("");
};

export const ResetPasswordDialog = ({ user, onClose }) => {
  const { logout } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${user.id}/reset-password`, { new_password: pw });
      if (data.self) {
        toast.success("Your password was changed. Sign in again with the new password.");
        await logout();
        window.location.href = "/login";
        return;
      }
      toast.success(`Password reset for ${user.name}. They've been signed out everywhere.`);
      onClose();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border-[#282E3D] bg-[#141721] text-slate-100 sm:max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl"><KeyRound className="h-5 w-5 text-[#FF6B00]" />Reset password</DialogTitle>
          <DialogDescription className="text-slate-400">Set a new password for <b className="text-slate-200">{user.name}</b> ({user.email}). Share it with them directly; they'll be signed out of any open sessions.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <input type={show ? "text" : "password"} autoComplete="new-password" placeholder="New password (min 6 characters)" data-testid="reset-password-input" className={`${inputCls} pr-10`} value={pw} onChange={(e) => setPw(e.target.value)} />
            <button type="button" data-testid="reset-password-toggle-visibility" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          </div>
          <input type={show ? "text" : "password"} autoComplete="new-password" placeholder="Confirm new password" data-testid="reset-password-confirm-input" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <button type="button" data-testid="reset-password-generate-button" onClick={() => { const g = generate(); setPw(g); setConfirm(g); setShow(true); }} className={`${btnGhost} w-full`}><Shuffle className="h-4 w-4" />Generate a strong password</button>
          <button data-testid="reset-password-submit-button" disabled={busy} className={`${btnPrimary} w-full`}>{busy ? "Resetting…" : "Reset password"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
